// enAble chart kit — EGDL chart chrome (see design Overview §3): hairline grid, 14 px tabular ticks,
// 2 px lines with dash by index, top legend with 24×3 swatches, square tooltip, 56 px brush with
// accent handles (keyboard ←/→), dashed event reference lines. Pure SVG so it can draw several
// thousand samples per series with decimation.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTheme } from '../ThemeContext';
import { PALETTE, dashOf } from './palette';

const v = (n) => `var(--egdl-${n})`;
const TICK = { fill: v('text-secondary'), font: `400 14px ${v('font')}`, fontVariantNumeric: 'tabular-nums' };

function useWidth(init = 640) {
    const ref = useRef(null);
    const [w, setW] = useState(init);
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        setW(Math.max(160, el.getBoundingClientRect().width));
        const ro = new ResizeObserver((e) => setW(Math.max(160, e[0].contentRect.width)));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    return [ref, w];
}

export function niceScale(min, max, count = 5) {
    if (min === max) { min -= 1; max += 1; }
    const s0 = (max - min) / count;
    const mag = Math.pow(10, Math.floor(Math.log10(s0)));
    const e = s0 / mag;
    const step = (e >= 7.5 ? 10 : e >= 3.5 ? 5 : e >= 1.5 ? 2 : 1) * mag;
    const lo = Math.floor(min / step) * step;
    const hi = Math.ceil(max / step) * step;
    const ticks = [];
    for (let t = lo; t <= hi + step / 2; t += step) ticks.push(+t.toFixed(10));
    return { lo, hi, ticks, step };
}
export const fmtNum = (x, step = 1) => (x == null ? '—' : x.toFixed(step >= 1 ? 0 : Math.min(3, Math.ceil(-Math.log10(step)))));
export function fmtTime(s) {
    const m = Math.floor(s / 60);
    const r = Math.round((s - m * 60) * 10) / 10;
    return m + ':' + (r < 10 ? '0' : '') + (r % 1 ? r.toFixed(1) : r);
}

function usePalette(themeProp) {
    const { theme } = useTheme();
    const t = themeProp || theme;
    return [PALETTE[t] || PALETTE.light, t];
}

export function Swatch({ idx = 0, width = 24, bar = false, color, theme }) {
    const [pal] = usePalette(theme);
    const c = color || pal[idx % 8];
    if (bar) return <span style={{ width: 12, height: 12, background: c, flexShrink: 0, display: 'inline-block' }} />;
    return (
        <svg width={width} height="10" style={{ flexShrink: 0, display: 'block' }} aria-hidden="true">
            <line x1="0" y1="5" x2={width} y2="5" stroke={c} strokeWidth="3" strokeDasharray={color ? '' : dashOf(idx)} />
        </svg>
    );
}

function Tooltip({ x, w, title, rows }) {
    const right = x > w - 280;
    return (
        <div role="status" style={{ position: 'absolute', top: 8, [right ? 'right' : 'left']: right ? w - x + 16 : x + 16, minWidth: 200, background: v('surface'), border: `1px solid ${v('border-strong')}`, padding: '12px 16px', pointerEvents: 'none', display: 'flex', flexDirection: 'column', gap: 6, zIndex: 2 }}>
            <span style={{ fontWeight: 600, fontSize: 14, letterSpacing: 1.7, textTransform: 'uppercase', color: v('text-secondary') }}>{title}</span>
            {rows.map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {r.idx != null || r.color ? <Swatch idx={r.idx ?? 0} color={r.color} width={16} bar={r.bar || !!r.color} /> : null}
                    <span style={{ flex: 1, fontSize: 15, color: v('text-tertiary'), whiteSpace: 'nowrap' }}>{r.label}</span>
                    <span style={{ fontWeight: 600, fontSize: 15, color: v('text-primary'), fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{r.value}</span>
                </div>
            ))}
        </div>
    );
}

export function Legend({ items, right }) {
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 20px', minHeight: 32 }}>
            {items.map((s, i) => (
                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 15, color: v('text-tertiary'), whiteSpace: 'nowrap' }}>
                    <Swatch idx={s.idx ?? i} bar={s.bar} color={s.color} />
                    {s.label}
                    {s.axis === 'right' ? <span style={{ color: v('text-secondary') }}>(R)</span> : null}
                </span>
            ))}
            <span style={{ flex: 1 }} />
            {right}
        </div>
    );
}

/**
 * MetricChart — line chart over a uniform time grid.
 * series: [{ label, unit?, axis?: 'left'|'right', dp?, idx?, data: (number|null)[] }] (all the same length)
 * duration: seconds spanned by the grid; playhead/onSeek in seconds;
 * markers: [{ t, label, color?, typeLabel? }] (a session event in its type's colour; gray without one).
 */
export function MetricChart({ series = [], duration = 60, height = 320, brush = true, leftUnit = '', rightUnit = '', xLabels = null, markers = [], legend = true, emptyText = 'No series selected', playhead = null, onSeek = null, theme }) {
    const [ref, w] = useWidth();
    const [pal] = usePalette(theme);
    const [win, setWin] = useState([0, 1]);
    const [hi, setHi] = useState(null);
    const n = series.length ? series[0].data.length : 0;
    useEffect(() => { setWin([0, 1]); }, [n]);
    useEffect(() => {
        if (playhead == null || !n) return;
        const f = playhead / duration;
        setWin((cur) => {
            if ((cur[0] > 0 || cur[1] < 1) && (f < cur[0] || f > cur[1])) {
                const sp = cur[1] - cur[0];
                const a = Math.max(0, Math.min(1 - sp, f - sp * 0.1));
                return [a, a + sp];
            }
            return cur;
        });
        // follow the playhead only when it moves
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playhead]);

    const hasR = series.some((s) => s.axis === 'right');
    const m = { l: 60, r: hasR ? 60 : 20, t: 30, b: 36 };
    const iw = Math.max(10, w - m.l - m.r);
    const ih = height - m.t - m.b;
    if (!n || n < 2) {
        return <div ref={ref} style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center', background: v('surface-sunken'), color: v('text-secondary'), fontSize: 17 }}>{emptyText}</div>;
    }
    const i0 = Math.floor(win[0] * (n - 1));
    const i1 = Math.max(i0 + 1, Math.ceil(win[1] * (n - 1)));
    const dom = (side) => {
        let lo = Infinity;
        let h = -Infinity;
        series.forEach((s) => {
            if ((s.axis || 'left') !== side) return;
            for (let i = i0; i <= i1; i++) {
                const x = s.data[i];
                if (x == null || !Number.isFinite(x)) continue;
                if (x < lo) lo = x;
                if (x > h) h = x;
            }
        });
        return lo === Infinity ? null : niceScale(lo, h, Math.max(3, Math.floor(ih / 56)));
    };
    const L = dom('left');
    const R = dom('right');
    const X = (i) => m.l + ((i - i0) / (i1 - i0)) * iw;
    const Y = (x, sc) => m.t + ih - ((x - sc.lo) / (sc.hi - sc.lo)) * ih;
    const st = Math.max(1, Math.floor((i1 - i0) / (iw * 1.5)));
    const scs = (s) => (s.axis === 'right' ? R : L);
    const path = (s) => {
        const sc = scs(s);
        if (!sc) return '';
        let d = '';
        for (let i = i0; i <= i1; i += st) {
            const x = s.data[i];
            if (x == null || !Number.isFinite(x)) continue;
            d += (d ? 'L' : 'M') + X(i).toFixed(1) + ',' + Y(x, sc).toFixed(1);
        }
        return d;
    };
    const tAt = (i) => (i / (n - 1)) * duration;
    const xt = [];
    if (xLabels) {
        const k = Math.max(1, Math.ceil((i1 - i0 + 1) / (iw / 96)));
        for (let i = i0; i <= i1; i += k) xt.push({ x: X(i), label: xLabels[i] });
    } else {
        niceScale(tAt(i0), tAt(i1), Math.max(2, Math.floor(iw / 110))).ticks.forEach((t) => {
            const i = (t / duration) * (n - 1);
            if (i >= i0 - 1e-6 && i <= i1 + 1e-6) xt.push({ x: X(i), label: fmtTime(t) });
        });
    }
    const fmtVal = (s, x) => (x == null || !Number.isFinite(x) ? '—' : x.toFixed(s.dp ?? 1) + (s.unit ? ' ' + s.unit : ''));
    const onMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        const px = e.clientX - r.left;
        setHi(px < m.l || px > m.l + iw ? null : Math.round(i0 + ((px - m.l) / iw) * (i1 - i0)));
    };
    const near = hi != null ? markers.find((mk) => Math.abs((mk.t / duration) * (n - 1) - hi) < (i1 - i0) / 60) : null;
    const zoomed = win[0] > 0 || win[1] < 1;
    const pi = playhead != null ? Math.round((playhead / duration) * (n - 1)) : null;
    const legendItems = pi != null ? series.map((s) => ({ ...s, label: s.label + '  ' + fmtVal(s, s.data[pi]) })) : series;
    const onClick = (e) => {
        if (!onSeek) return;
        const r = e.currentTarget.getBoundingClientRect();
        const px = e.clientX - r.left;
        if (px < m.l || px > m.l + iw) return;
        onSeek(tAt(i0 + ((px - m.l) / iw) * (i1 - i0)));
    };
    // brush
    const bh = 56;
    const BX = (f) => m.l + f * iw;
    const drag = (mode) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        const svg = e.currentTarget.ownerSVGElement || e.currentTarget;
        const r = svg.getBoundingClientRect();
        let w0 = [...win];
        if (mode === 'bg') {
            const f = Math.min(1, Math.max(0, (e.clientX - r.left - m.l) / iw));
            const sp = zoomed ? w0[1] - w0[0] : 0.2;
            const a = Math.max(0, Math.min(1 - sp, f - sp / 2));
            w0 = [a, a + sp];
            setWin(w0);
            mode = 'move';
        }
        const x0 = e.clientX;
        const mv = (ev) => {
            const df = (ev.clientX - x0) / iw;
            let [a, b] = w0;
            if (mode === 'l') a = Math.min(b - 0.02, Math.max(0, a + df));
            else if (mode === 'r') b = Math.max(a + 0.02, Math.min(1, b + df));
            else {
                const sp = b - a;
                a = Math.max(0, Math.min(1 - sp, a + df));
                b = a + sp;
            }
            setWin([a, b]);
        };
        const up = () => {
            window.removeEventListener('pointermove', mv);
            window.removeEventListener('pointerup', up);
        };
        window.addEventListener('pointermove', mv);
        window.addEventListener('pointerup', up);
    };
    const keys = (mode) => (e) => {
        const d = e.key === 'ArrowLeft' ? -0.02 : e.key === 'ArrowRight' ? 0.02 : 0;
        if (!d) return;
        e.preventDefault();
        let [a, b] = win;
        if (mode === 'l') a = Math.min(b - 0.02, Math.max(0, a + d));
        else b = Math.max(a + 0.02, Math.min(1, b + d));
        setWin([a, b]);
    };
    const ov = series.slice(0, 3).map((s, k) => {
        let lo = Infinity;
        let h = -Infinity;
        s.data.forEach((x) => {
            if (x == null || !Number.isFinite(x)) return;
            lo = Math.min(lo, x);
            h = Math.max(h, x);
        });
        const stp = Math.max(1, Math.floor(n / iw));
        let d = '';
        for (let i = 0; i < n; i += stp) {
            const x = s.data[i];
            if (x == null || !Number.isFinite(x)) continue;
            d += (d ? 'L' : 'M') + BX(i / (n - 1)).toFixed(1) + ',' + (6 + (bh - 12) * (1 - (x - lo) / (h - lo || 1))).toFixed(1);
        }
        return { d, idx: s.idx ?? k };
    });
    const grid = L || R;
    const denseMarkers = markers.filter((mk) => { const i = (mk.t / duration) * (n - 1); return i >= i0 && i <= i1; }).length > iw / 24;
    return (
        <div ref={ref} style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
            {legend ? (
                <Legend
                    items={legendItems}
                    right={zoomed ? <button type="button" onClick={() => setWin([0, 1])} className="eg-btn eg-btn--ghost" style={{ height: 40 }}>Reset zoom</button> : null}
                />
            ) : null}
            <div style={{ position: 'relative' }}>
                <svg width={w} height={height} onMouseMove={onMove} onClick={onClick} onMouseLeave={() => setHi(null)} style={{ display: 'block', overflow: 'visible', cursor: onSeek ? 'pointer' : 'default' }} role="img" aria-label={'Line chart: ' + series.map((s) => s.label).join(', ')}>
                    {grid ? grid.ticks.map((t, k) => <line key={k} x1={m.l} x2={m.l + iw} y1={Y(t, grid)} y2={Y(t, grid)} style={{ stroke: v('hairline') }} />) : null}
                    {L ? L.ticks.map((t, k) => <text key={k} x={m.l - 10} y={Y(t, L) + 5} textAnchor="end" style={TICK}>{fmtNum(t, L.step)}</text>) : null}
                    {R ? R.ticks.map((t, k) => <text key={k} x={m.l + iw + 10} y={Y(t, R) + 5} style={TICK}>{fmtNum(t, R.step)}</text>) : null}
                    {leftUnit && L ? <text x={0} y={12} style={{ ...TICK, fontWeight: 600 }}>{leftUnit}</text> : null}
                    {rightUnit && R ? <text x={w} y={12} textAnchor="end" style={{ ...TICK, fontWeight: 600 }}>{rightUnit}</text> : null}
                    <line x1={m.l} x2={m.l + iw} y1={m.t + ih} y2={m.t + ih} style={{ stroke: v('border-strong') }} />
                    {xt.map((t, k) => (
                        <g key={k}>
                            <line x1={t.x} x2={t.x} y1={m.t + ih} y2={m.t + ih + 6} style={{ stroke: v('border-strong') }} />
                            <text x={t.x} y={m.t + ih + 24} textAnchor="middle" style={TICK}>{t.label}</text>
                        </g>
                    ))}
                    {markers.map((mk, k) => {
                        const i = (mk.t / duration) * (n - 1);
                        if (i < i0 || i > i1) return null;
                        const x = X(i);
                        return (
                            <g key={'m' + k}>
                                {/* Reference lines only while they stay readable; dense events keep just the top marks.
                                    A game's colour is drawn as given, so the mark keeps a hairline edge in any theme. */}
                                {denseMarkers ? null : <line x1={x} x2={x} y1={m.t} y2={m.t + ih} strokeDasharray="3 4" strokeOpacity={mk.color ? 0.75 : 1} style={{ stroke: mk.color || v('border-hover') }} />}
                                <rect x={x - 4} y={m.t - 4} width="8" height="8" style={{ fill: mk.color || v('text-secondary'), stroke: v('border-strong'), strokeWidth: 1 }} />
                            </g>
                        );
                    })}
                    {series.map((s, k) => <path key={k} d={path(s)} fill="none" stroke={pal[(s.idx ?? k) % 8]} strokeWidth="2" strokeDasharray={dashOf(s.idx ?? k)} strokeLinejoin="round" />)}
                    {pi != null && pi >= i0 && pi <= i1 ? (
                        <g pointerEvents="none">
                            <line x1={X(pi)} x2={X(pi)} y1={m.t - 6} y2={m.t + ih} style={{ stroke: v('accent'), strokeWidth: 2 }} />
                            <rect x={X(pi) - 6} y={m.t - 14} width="12" height="10" style={{ fill: v('accent') }} />
                            {series.map((s, k) => {
                                const sc = scs(s);
                                const x = s.data[pi];
                                return sc && x != null && Number.isFinite(x) ? <rect key={k} x={X(pi) - 4} y={Y(x, sc) - 4} width="8" height="8" fill={pal[(s.idx ?? k) % 8]} style={{ stroke: v('accent'), strokeWidth: 2 }} /> : null;
                            })}
                        </g>
                    ) : null}
                    {hi != null ? (
                        <g pointerEvents="none">
                            <line x1={X(hi)} x2={X(hi)} y1={m.t} y2={m.t + ih} style={{ stroke: v('border-hover') }} />
                            {series.map((s, k) => {
                                const sc = scs(s);
                                const x = s.data[hi];
                                return sc && x != null && Number.isFinite(x) ? <rect key={k} x={X(hi) - 4} y={Y(x, sc) - 4} width="8" height="8" fill={pal[(s.idx ?? k) % 8]} style={{ stroke: v('surface'), strokeWidth: 2 }} /> : null;
                            })}
                        </g>
                    ) : null}
                </svg>
                {hi != null ? (
                    <Tooltip
                        x={X(hi)}
                        w={w}
                        title={xLabels ? xLabels[hi] : fmtTime(tAt(hi))}
                        rows={[
                            ...series.map((s, k) => ({ idx: s.idx ?? k, label: s.label, value: fmtVal(s, s.data[hi]) })),
                            ...(near ? [{ color: near.color || undefined, label: (near.typeLabel || 'Event') + ' · ' + near.label, value: fmtTime(near.t) }] : []),
                        ]}
                    />
                ) : null}
            </div>
            {brush ? (
                <svg width={w} height={bh} style={{ display: 'block', touchAction: 'none' }} aria-label="Zoom range">
                    <rect x={m.l} y="0" width={iw} height={bh} onPointerDown={drag('bg')} style={{ fill: v('surface-sunken'), cursor: 'crosshair' }} />
                    {ov.map((o, k) => <path key={k} d={o.d} fill="none" stroke={pal[o.idx % 8]} strokeOpacity=".55" strokeWidth="1.5" pointerEvents="none" />)}
                    <rect x={m.l} y="0" width={BX(win[0]) - m.l} height={bh} pointerEvents="none" style={{ fill: v('background'), opacity: 0.65 }} />
                    <rect x={BX(win[1])} y="0" width={m.l + iw - BX(win[1])} height={bh} pointerEvents="none" style={{ fill: v('background'), opacity: 0.65 }} />
                    <rect x={BX(win[0])} y="0.5" width={Math.max(1, BX(win[1]) - BX(win[0]))} height={bh - 1} onPointerDown={drag('move')} style={{ fill: 'transparent', stroke: v('accent'), cursor: 'grab' }} />
                    {playhead != null ? <line x1={BX(playhead / duration)} x2={BX(playhead / duration)} y1="0" y2={bh} pointerEvents="none" style={{ stroke: v('accent'), strokeWidth: 2 }} /> : null}
                    {[['l', win[0]], ['r', win[1]]].map(([md, f]) => (
                        <g key={md} tabIndex="0" role="slider" aria-label={md === 'l' ? 'Zoom start' : 'Zoom end'} aria-valuenow={Math.round(f * 100)} aria-valuemin={0} aria-valuemax={100} onKeyDown={keys(md)} onPointerDown={drag(md)} style={{ cursor: 'ew-resize', outline: 'none' }}>
                            <rect x={BX(f) - 16} y="0" width="32" height={bh} fill="transparent" />
                            <rect x={BX(f) - 8} y={(bh - 32) / 2} width="16" height="32" style={{ fill: v('accent') }} />
                            <line x1={BX(f) - 2} x2={BX(f) - 2} y1={bh / 2 - 7} y2={bh / 2 + 7} style={{ stroke: v('accent-text') }} />
                            <line x1={BX(f) + 2} x2={BX(f) + 2} y1={bh / 2 - 7} y2={bh / 2 + 7} style={{ stroke: v('accent-text') }} />
                        </g>
                    ))}
                </svg>
            ) : null}
        </div>
    );
}

/** BarChart — grouped bars; categories on x, series [{ label, idx?, data: number[] }]. */
export function BarChart({ categories = [], series = [], height = 280, max = null, unit = '', theme, dp = 1 }) {
    const [ref, w] = useWidth();
    const [pal] = usePalette(theme);
    const [hi, setHi] = useState(null);
    const m = { l: 48, r: 16, t: 12, b: 36 };
    const iw = Math.max(10, w - m.l - m.r);
    const ih = height - m.t - m.b;
    let top = max;
    if (top == null) {
        top = 0;
        series.forEach((s) => s.data.forEach((x) => { if (Number.isFinite(x)) top = Math.max(top, x); }));
    }
    const sc = niceScale(0, top || 1, 4);
    const Y = (x) => m.t + ih - (x / sc.hi) * ih;
    const gw = iw / Math.max(1, categories.length);
    const ns = Math.max(1, series.length);
    const bw = Math.min(28, (gw * 0.7) / ns);
    return (
        <div ref={ref} style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
            <Legend items={series.map((s, k) => ({ label: s.label, idx: s.idx ?? k, bar: true }))} />
            <div style={{ position: 'relative' }}>
                <svg width={w} height={height} style={{ display: 'block' }} onMouseLeave={() => setHi(null)} role="img" aria-label={'Bar chart: ' + series.map((s) => s.label).join(', ')}>
                    {sc.ticks.map((t, k) => (
                        <g key={k}>
                            <line x1={m.l} x2={m.l + iw} y1={Y(t)} y2={Y(t)} style={{ stroke: k ? v('hairline') : v('border-strong') }} />
                            <text x={m.l - 10} y={Y(t) + 5} textAnchor="end" style={TICK}>{fmtNum(t, sc.step)}</text>
                        </g>
                    ))}
                    {categories.map((c, ci) => {
                        const gx = m.l + ci * gw;
                        return (
                            <g key={ci} onMouseEnter={() => setHi(ci)}>
                                <rect x={gx} y={m.t} width={gw} height={ih} style={{ fill: hi === ci ? v('row-hover') : 'transparent' }} />
                                {series.map((s, si) => {
                                    const x = Number.isFinite(s.data[ci]) ? s.data[ci] : 0;
                                    return <rect key={si} x={gx + gw / 2 - (ns * bw) / 2 + si * bw + 1} y={Y(x)} width={Math.max(1, bw - 2)} height={Y(0) - Y(x)} fill={pal[(s.idx ?? si) % 8]} />;
                                })}
                                <text x={gx + gw / 2} y={m.t + ih + 24} textAnchor="middle" style={TICK}>{c.length > gw / 8 ? c.slice(0, Math.max(3, Math.floor(gw / 9))) + '…' : c}</text>
                            </g>
                        );
                    })}
                </svg>
                {hi != null ? (
                    <Tooltip
                        x={m.l + (hi + 0.5) * gw}
                        w={w}
                        title={categories[hi]}
                        rows={series.map((s, k) => ({ idx: s.idx ?? k, bar: true, label: s.label, value: Number.isFinite(s.data[hi]) ? s.data[hi].toFixed(dp) + (unit ? ' ' + unit : '') : '—' }))}
                    />
                ) : null}
            </div>
        </div>
    );
}

/** Sparkline — fixed-height trace; color overrides the series palette (used for player colours). */
export function Sparkline({ data = [], idx = 0, height = 36, min, max, color, step, theme }) {
    const [pal] = usePalette(theme);
    const pts = data.filter((x) => Number.isFinite(x));
    if (pts.length < 2) return <div style={{ height, flex: 1, minWidth: 0 }} />;
    const lo = min ?? Math.min(...pts);
    const hi = max ?? Math.max(...pts);
    const N = data.length;
    let d = '';
    data.forEach((x, i) => {
        if (!Number.isFinite(x)) return;
        const px = ((i / (N - 1)) * 100).toFixed(2);
        const py = (2 + (height - 4) * (1 - (x - lo) / (hi - lo || 1))).toFixed(2);
        if (!d) d = 'M' + px + ',' + py;
        else d += step ? 'H' + px + 'V' + py : 'L' + px + ',' + py;
    });
    return (
        <svg viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" style={{ width: '100%', height, display: 'block', flex: 1, minWidth: 0 }} aria-hidden="true">
            <line x1="0" x2="100" y1={height - 1} y2={height - 1} style={{ stroke: v('hairline') }} vectorEffect="non-scaling-stroke" />
            <path d={d} fill="none" stroke={color || pal[idx % 8]} strokeWidth="2" vectorEffect="non-scaling-stroke" strokeDasharray={color ? '' : dashOf(idx)} />
        </svg>
    );
}

/** Location2D — top-down plot with a trail; trail points [x, z] in metres around the origin. */
export function Location2D({ trail = [], height = 240, extent = 1.25, color, label = 'Top-down location' }) {
    const [pal] = usePalette();
    const P = trail.map(([x, z]) => [50 + (x / extent) * 40, 50 + (z / extent) * 40]);
    return (
        <svg viewBox="0 0 100 100" style={{ width: '100%', height, display: 'block', background: v('surface-sunken') }} preserveAspectRatio="xMidYMid meet" role="img" aria-label={label}>
            {[10, 30, 50, 70, 90].map((k) => (
                <g key={k}>
                    <line x1={k} x2={k} y1="5" y2="95" style={{ stroke: k === 50 ? v('border-strong') : v('hairline') }} vectorEffect="non-scaling-stroke" />
                    <line y1={k} y2={k} x1="5" x2="95" style={{ stroke: k === 50 ? v('border-strong') : v('hairline') }} vectorEffect="non-scaling-stroke" />
                </g>
            ))}
            {P.length > 1 ? <polyline points={P.map((p) => p.join(',')).join(' ')} fill="none" stroke={color || pal[0]} strokeOpacity=".7" strokeWidth="1.5" vectorEffect="non-scaling-stroke" /> : null}
            {P.length ? <rect x={P[P.length - 1][0] - 2.5} y={P[P.length - 1][1] - 2.5} width="5" height="5" style={{ fill: color || v('accent') }} /> : null}
        </svg>
    );
}

/**
 * Resample timestamped samples onto a uniform grid for MetricChart.
 * samples: [{ t: seconds, v: number }]; returns an array (length (t1 - t0)·hz + 1) with null where
 * no sample falls in a bin (bins average their samples).
 */
export function toUniformGrid(samples, t0, t1, hz = 10) {
    const n = Math.max(2, Math.round((t1 - t0) * hz) + 1);
    const sum = new Array(n).fill(0);
    const cnt = new Array(n).fill(0);
    samples.forEach(({ t, v: val }) => {
        if (!Number.isFinite(val)) return;
        const i = Math.round((t - t0) * hz);
        if (i < 0 || i >= n) return;
        sum[i] += val;
        cnt[i] += 1;
    });
    return sum.map((s, i) => (cnt[i] ? s / cnt[i] : null));
}

/** Average a uniform series into bins of `bin` samples (timescale 1 s / 10 s), keeping length. */
export function binAverage(a, bin) {
    if (bin <= 1) return a;
    const out = new Array(a.length);
    for (let i = 0; i < a.length; i += bin) {
        let s = 0;
        let c = 0;
        for (let j = i; j < Math.min(a.length, i + bin); j++) if (a[j] != null) { s += a[j]; c++; }
        const avg = c ? +(s / c).toFixed(4) : null;
        for (let j = i; j < Math.min(a.length, i + bin); j++) out[j] = avg;
    }
    return out;
}
